import { Abi, AbiEvent } from 'abitype/zod'
import { type AbiEvent as AbiEventType, decodeEventLog, getAbiItem } from 'viem'
import type { Hex } from '../../../types'
import type { Log } from '../../../types/log'

type DecodeLogParameters = {
  log: Log
  addressAbi?: string
  eventAbis: string[]
}

export const decodeLog = ({
  log,
  addressAbi,
  eventAbis,
}: DecodeLogParameters): Log => {
  // address ABI があれば、 address ABI を使用して　decode
  if (addressAbi !== undefined) {
    const decoded = decodeLogByAddressAbi(log, addressAbi)
    if (decoded !== undefined) {
      return decoded
    }
  }

  // address ABI で decode できない場合、 event ABI を使用して decode
  for (const eventAbi of eventAbis) {
    const decoded = decodeInputByEventAbi(log, eventAbi)
    if (decoded !== undefined) {
      return decoded
    }
  }

  // decode できない場合、 log をそのまま返す
  return log
}

export const decodeLogByAddressAbi = (
  log: Log,
  abiText: string,
): Log | undefined => {
  try {
    const parsedAbi = Abi.safeParse(JSON.parse(abiText))
    if (!parsedAbi.success) {
      console.debug(parsedAbi.error)
      return undefined
    }
    const abi = parsedAbi.data.filter((v) => v.type === 'event')
    const decodedLog = decodeEventLog({
      abi,
      topics: log.topics as [Hex, ...Hex[]] | [],
      data: log.data,
    })
    const abiItem = getAbiItem({
      abi,
      name: decodedLog.eventName,
    })
    return {
      ...log,
      eventName: decodedLog.eventName,
      args: createLogArgs(decodedLog.args, abiItem),
    }
  } catch (error) {
    console.debug(error)
    return undefined
  }
}

const decodeInputByEventAbi = (log: Log, abiText: string): Log | undefined => {
  try {
    const parsedAbi = AbiEvent.safeParse(JSON.parse(abiText))
    if (!parsedAbi.success) {
      console.debug(parsedAbi.error)
      return undefined
    }
    const abiItem = parsedAbi.data
    const decodedLog = decodeEventLog({
      abi: [abiItem],
      topics: log.topics as [Hex, ...Hex[]] | [],
      data: log.data,
    })
    return {
      ...log,
      eventName: decodedLog.eventName,
      args: createLogArgs(decodedLog.args, abiItem),
    }
  } catch (error) {
    console.debug(error)
    return undefined
  }
}

const createLogArgs = (
  args: Record<string, unknown> | unknown[],
  abiItem: AbiEventType,
) =>
  abiItem.inputs.map(({ indexed, name, type }, index) => ({
    indexed: indexed ?? false,
    name: name ?? '',
    type: type,
    value: Array.isArray(args) ? args[index] : args[name ?? ''],
  }))
